import { useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { lifecycle } from '../platform/lifecycle';
import { network } from '../platform/network';
import {
  socket as authenticatedSocket,
  getConfirmedSocketIdentity,
  reconcileAuthenticatedRealtimeSession,
  setRealtimeBackgroundState,
  setRealtimeNetworkOffline,
  setRealtimeNetworkOnline,
} from '../services/socket';
import { createRealtimeSessionCoordinator } from '../services/realtimeSessionCoordinator';

function userIdOf(user) {
  return String(user?._id || user?.id || user?.userId || '').trim();
}

/**
 * The sole application owner of authenticated realtime lifecycle recovery.
 * Feature screens declare desired subscriptions through socket.js; they do not
 * connect, authenticate, or react to native lifecycle/network signals.
 */
export default function AuthenticatedSocketLifecycle() {
  const { token, user, isAuthReady } = useAuth();
  const userId = userIdOf(user);
  const authGeneration = Number(authenticatedSocket?.__z10_auth_generation || 0);
  const hasAuthenticatedSession = Boolean(isAuthReady && token && userId);
  const announcedTupleRef = useRef('');
  const coordinatorRef = useRef(null);

  if (!coordinatorRef.current) {
    coordinatorRef.current = createRealtimeSessionCoordinator({
      lifecycle,
      network,
      onBackground: setRealtimeBackgroundState,
      onOffline: setRealtimeNetworkOffline,
      onOnline: setRealtimeNetworkOnline,
      reconcile: async (context) => {
        const recovered = await reconcileAuthenticatedRealtimeSession({
          source: context.reason,
          expectedUserId: context.userId,
        });
        if (!recovered || !context.isCurrent()) return false;
        const identity = getConfirmedSocketIdentity(context.userId);
        if (!identity) return false;
        const tuple = `${identity.userId}:${identity.socketId}:${identity.authVersion}`;
        if (announcedTupleRef.current !== tuple) {
          announcedTupleRef.current = tuple;
          window.__z10_confirmed_socket_identity__ = identity;
          window.dispatchEvent(new CustomEvent('z10:socketIdentityConfirmed', { detail: identity }));
        }
        return true;
      },
    });
  }

  useEffect(() => {
    const coordinator = coordinatorRef.current;
    coordinator.start();
    const onConnect = () => { void coordinator.request('socket-connect'); };
    const onDisconnect = () => { announcedTupleRef.current = ''; };
    authenticatedSocket.on('connect', onConnect);
    authenticatedSocket.on('disconnect', onDisconnect);
    return () => {
      authenticatedSocket.off('connect', onConnect);
      authenticatedSocket.off('disconnect', onDisconnect);
      coordinator.stop();
    };
  }, []);

  useEffect(() => {
    const coordinator = coordinatorRef.current;
    if (!hasAuthenticatedSession) {
      announcedTupleRef.current = '';
      void coordinator.clearSession();
      return;
    }
    void coordinator.setSession({ userId, token, authGeneration });
  }, [hasAuthenticatedSession, userId, token, authGeneration]);

  return null;
}
